import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Link2, CheckCircle2, PlugZap, MessageSquare, Video, Calendar, Mail, HardDrive, CalendarClock, Youtube, Banknote } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import DOMPurify from "dompurify";
import { toast } from "sonner";

import { useQuery } from "@tanstack/react-query";
import { money } from "@/lib/format";

const HEALTH_UI = {
  connected: "bg-[#5C8A72]/15 text-[#5C8A72]",
  attention: "bg-[#D98E73]/20 text-[#B5794E]",
  in_progress: "bg-[#D98E73]/20 text-[#B5794E]",
  disconnected: "bg-[#F1EEE8] text-[#8FA89B]",
  not_configured: "bg-[#F1EEE8] text-[#8FA89B]",
};
const HEALTH_LABEL = { connected: "OK", attention: "Attention", in_progress: "In progress", disconnected: "—", not_configured: "n/a" };

function HCell({ status }) {
  return <span className={`text-[11px] px-2 py-0.5 rounded-full ${HEALTH_UI[status] || HEALTH_UI.disconnected}`}>{HEALTH_LABEL[status] || status}</span>;
}

function EnhancerIntegrationHealth() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-int-health"], queryFn: () => api.get("/admin/integrations/health").then((r) => r.data) });
  const rows = data?.enhancers || [];
  return (
    <div className="eyw-card p-6" data-testid="admin-integration-health">
      <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-1"><PlugZap size={18} className="text-[#144F75]" /> Enhancer integration health</h3>
      <p className="text-sm text-[#4A5D6B] mb-4">Connection status per enhancer — no credentials are shown. Enhancers manage their own connections from their dashboard.</p>
      {isLoading ? <div className="text-[#8FA89B] text-sm"><Loader2 size={14} className="animate-spin inline" /> Loading…</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="int-health-table">
            <thead><tr className="text-left text-xs text-[#8FA89B] border-b border-[#E5E0D8]">
              <th className="py-2 pr-4">Enhancer</th><th className="py-2 px-2">Calendar</th><th className="py-2 px-2">Zoom</th><th className="py-2 px-2">Stripe</th><th className="py-2 px-2">YouTube</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.enhancer_id} className={`border-b border-[#F1EEE8] ${r.attention ? "bg-[#D98E73]/[0.04]" : ""}`} data-testid={`int-health-row-${r.enhancer_id}`}>
                  <td className="py-2 pr-4"><div className="font-medium text-[#1A242B]">{r.name}</div><div className="text-[11px] text-[#8FA89B]">{r.email}</div></td>
                  <td className="py-2 px-2"><HCell status={r.google_calendar} /></td>
                  <td className="py-2 px-2"><HCell status={r.zoom} /></td>
                  <td className="py-2 px-2"><HCell status={r.stripe} /></td>
                  <td className="py-2 px-2"><HCell status={r.youtube} /></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-[#8FA89B]">No enhancers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PolicyOverridesCard() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-policy-overrides"], queryFn: () => api.get("/admin/policy-overrides").then((r) => r.data) });
  const d = data?.default || {};
  const groups = [
    { key: "services", label: "Services", items: data?.services || [] },
    { key: "centers", label: "Centers", items: data?.centers || [] },
    { key: "enhancers", label: "Enhancers", items: data?.enhancers || [] },
  ];
  return (
    <div className="eyw-card p-6" data-testid="policy-overrides-card">
      <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-1"><CalendarClock size={18} className="text-[#144F75]" /> Policy overrides</h3>
      <p className="text-sm text-[#4A5D6B] mb-4">Every service, center &amp; Enhancer whose booking/cancellation terms differ from the shop-wide default. Edit a service's policy on its Offerings form; center/Enhancer policies from their own dashboards.</p>
      {isLoading ? (
        <div className="text-[#8FA89B] text-sm"><Loader2 size={14} className="animate-spin inline" /> Loading…</div>
      ) : (
        <>
          <div className="rounded-xl bg-[#144F75]/[0.05] border border-[#144F75]/10 px-4 py-3 mb-4 text-sm text-[#1A242B]" data-testid="policy-default-summary">
            <span className="font-medium">Center default:</span> cancel window <b>{d.cancel_window_hours || 0}h</b> · late fee <b>{money(Number(d.cancel_fee || 0))}</b> · min notice <b>{d.min_notice_hours || 0}h</b>
          </div>
          {(data?.total || 0) === 0 ? (
            <p className="text-sm text-[#8FA89B]" data-testid="policy-overrides-empty">Everything uses the default policy — no overrides set.</p>
          ) : (
            <div className="space-y-5">
              {groups.filter((g) => g.items.length > 0).map((g) => (
                <div key={g.key} data-testid={`policy-group-${g.key}`}>
                  <div className="text-xs uppercase tracking-wide text-[#8FA89B] mb-2">{g.label} · {g.items.length}</div>
                  <div className="space-y-2">
                    {g.items.map((it) => (
                      <div key={it.id} className="flex flex-wrap items-center gap-2 border-b border-[#F1EEE8] pb-2" data-testid={`policy-override-${it.id}`}>
                        <span className="text-sm font-medium text-[#1A242B] min-w-[160px]">{it.name}</span>
                        <div className="flex flex-wrap gap-1.5">
                          {it.diffs.map((df) => (
                            <span key={df.field} className="inline-flex items-center gap-1 text-xs bg-[#B5794E]/[0.12] text-[#8a5a37] px-2.5 py-1 rounded-full">
                              {df.label}: <b>{df.value}</b> <span className="text-[#B5794E]/70">(default {df.default})</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, hint }) {
  return (
    <div>
      <label className="text-xs text-[#4A5D6B] block mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="eyw-input w-full" />
      {hint && <p className="text-[11px] text-[#8FA89B] mt-1">{hint}</p>}
    </div>
  );
}

export default function IntegrationsPanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [drive, setDrive] = useState(null);
  const [driveTesting, setDriveTesting] = useState(false);
  const [notice, setNotice] = useState(12);
  const [cancelWindow, setCancelWindow] = useState(0);
  const [cancelFee, setCancelFee] = useState(0);
  const [savingNotice, setSavingNotice] = useState(false);

  const loadNotice = async () => {
    try {
      const { data } = await api.get("/booking-policy");
      setNotice(data.min_notice_hours ?? 12);
      setCancelWindow(data.cancel_window_hours ?? 0);
      setCancelFee(data.cancel_fee ?? 0);
    } catch { /* ignore */ }
  };
  const saveNotice = async () => {
    setSavingNotice(true);
    try {
      await api.put("/admin/booking-policy", {
        min_notice_hours: Number(notice),
        cancel_window_hours: Number(cancelWindow),
        cancel_fee: Number(cancelFee),
      });
      toast.success("Booking policy saved.");
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingNotice(false); }
  };
  const [params, setParams] = useSearchParams();

  const loadDrive = async () => {
    try { const { data } = await api.get("/admin/integrations/drive/status"); setDrive(data); } catch { /* ignore */ }
  };
  const testDrive = async () => {
    setDriveTesting(true);
    try {
      const { data } = await api.post("/admin/integrations/drive/test");
      if (data.ok) toast.success("Backup succeeded — a test PDF was uploaded to your Drive.");
      else toast.error(data.detail || data.error || "Connect Google Drive first.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDriveTesting(false); }
  };
  const connectDrive = async () => {
    try { const { data } = await api.get("/google/drive/connect"); if (data.url) window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disconnectDrive = async () => {
    try { await api.post("/admin/integrations/drive/disconnect"); toast.success("Google Drive disconnected."); loadDrive(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    const { data } = await api.get("/admin/integrations");
    setCfg(data);
    setForm({
      google_client_id: data.google_client_id || "",
      google_client_secret: "",
      google_calendar_id: data.google_calendar_id || "primary",
      google_chat_webhook: "",
      google_chat_bookings_webhook: "",
      google_chat_sales_webhook: "",
      zoom_account_id: data.zoom_account_id || "",
      zoom_client_id: data.zoom_client_id || "",
      zoom_client_secret: "",
      resend_api_key: "",
      resend_from_email: data.resend_from_email || "",
      youtube_api_key: "",
      digest_enabled: data.digest_enabled !== false,
      digest_day: data.digest_day ?? 1,
      digest_hour: data.digest_hour ?? 9,
      renewal_reminder_days: data.renewal_reminder_days ?? 3,
      twilio_account_sid: data.twilio_account_sid || "",
      twilio_auth_token: "",
      twilio_api_key_sid: data.twilio_api_key_sid || "",
      twilio_api_key_secret: "",
      twilio_from: data.twilio_from || "",
      receipt_promo: data.receipt_promo || "",
      min_exempt_promo_codes: data.min_exempt_promo_codes || "",
      low_stock_email: data.low_stock_email || "",
      paypal_client_id: data.paypal_client_id || "",
      paypal_secret: "",
      paypal_mode: data.paypal_mode || "sandbox",
    });
  };
  useEffect(() => { load(); loadDrive(); loadNotice(); }, []);

  useEffect(() => {
    const s = params.get("integrations");
    if (!s) return;
    if (s === "google_connected") toast.success("Google Calendar connected!");
    else if (s.includes("error")) toast.error("Google connection failed — check your credentials and redirect URI.");
    params.delete("integrations"); setParams(params, { replace: true });
    load();
  }, [params, setParams]);

  useEffect(() => {
    const g = params.get("gdrive");
    if (!g) return;
    if (g === "connected") toast.success("Google Drive connected — auto-backup is on.");
    else toast.error("Google Drive connection failed — check the Drive API, redirect URI & consent screen.");
    params.delete("gdrive"); setParams(params, { replace: true });
    loadDrive();
  }, [params, setParams]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/integrations", form);
      toast.success("Integration settings saved");
      await load();
      setForm((f) => ({ ...f, google_client_secret: "", google_chat_webhook: "", google_chat_bookings_webhook: "", google_chat_sales_webhook: "", zoom_client_secret: "", resend_api_key: "", twilio_auth_token: "", twilio_api_key_secret: "", youtube_api_key: "", paypal_secret: "" }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const connectGoogle = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/oauth/calendar/login");
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save your Google credentials first."); setConnecting(false); }
  };

  const disconnectGoogle = async () => {
    await api.post("/admin/integrations/google/disconnect");
    toast.success("Disconnected");
    load();
  };

  const testChat = async () => {
    try { await api.post("/admin/integrations/chat/test"); toast.success("Test message sent to Google Chat"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const testZoom = async () => {
    try { const { data } = await api.post("/admin/integrations/zoom/test"); toast.success(data.message || "Zoom verified"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const testEmail = async () => {
    const to = window.prompt("Send a test email to (in Resend test mode, use your own verified address):");
    if (!to) return;
    try { const { data } = await api.post("/admin/integrations/email/test", { to }); toast.success(data.message || "Sent"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const testPaypal = async () => {
    try { const { data } = await api.post("/admin/integrations/paypal/test"); toast.success(data.message || "PayPal connected"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const previewDigest = async () => {
    try {
      const { data } = await api.get("/pos/weekly-digest/preview");
      const safeHtml = DOMPurify.sanitize(data.html);
      const doc = `<!doctype html><html><head><meta charset="utf-8"><title>Weekly digest preview</title></head><body style="margin:0"><div style="background:#F4F1EB;padding:24px;min-height:100vh"><p style="font-family:sans-serif;color:#4A5D6B;max-width:560px;margin:0 auto 16px">Preview only — this would go to <b>${Number(data.recipient_count) || 0}</b> opted-in member(s). ${Number(data.post_count) || 0} post(s), ${Number(data.event_count) || 0} upcoming gathering(s).</p>${safeHtml}</div></body></html>`;
      const blob = new Blob([doc], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const testSms = async () => {
    const to = window.prompt("Send a test SMS to (E.164 format, e.g. +15551234567):");
    if (!to) return;
    try { const { data } = await api.post("/admin/integrations/sms/test", { to }); toast.success(data.message || "Sent"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!cfg) return <div className="py-16 text-center text-[#4A5D6B]"><Loader2 className="animate-spin inline" /></div>;

  return (
    <div className="space-y-6" data-testid="integrations-panel">
      <EnhancerIntegrationHealth />
      {/* Google Calendar */}
      <div className="eyw-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Calendar size={18} className="text-[#144F75]" /> Google Calendar</h3>
          {cfg.google_connected
            ? <span className="text-xs inline-flex items-center gap-1 bg-[#5C8A72]/15 text-[#5C8A72] px-3 py-1 rounded-full" data-testid="google-status"><CheckCircle2 size={13} /> Connected {cfg.google_connected_email && `· ${cfg.google_connected_email}`}</span>
            : <span className="text-xs bg-[#D98E73]/15 text-[#B5794E] px-3 py-1 rounded-full" data-testid="google-status">Not connected</span>}
        </div>
        <p className="text-sm text-[#4A5D6B] mb-4">Appointments & bookings will automatically sync to your Google Calendar (create / reschedule / cancel). Add your OAuth credentials, save, then connect.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Google Client ID" value={form.google_client_id} onChange={(v) => setForm({ ...form, google_client_id: v })} placeholder="xxxx.apps.googleusercontent.com" testid="google-client-id" />
          <Field label="Google Client Secret" type="password" value={form.google_client_secret} onChange={(v) => setForm({ ...form, google_client_secret: v })} placeholder={cfg.google_client_secret_set ? "•••••• (saved — leave blank to keep)" : "GOCSPX-..."} testid="google-client-secret" />
          <Field label="Target calendar ID" value={form.google_calendar_id} onChange={(v) => setForm({ ...form, google_calendar_id: v })} placeholder="primary" testid="google-calendar-id" hint="Use 'primary', or paste a specific calendar ID for a dedicated EYW calendar." />
        </div>
        <p className="text-[11px] text-[#8FA89B] mt-3">Redirect URI to register in Google Cloud: <code className="text-[#144F75]">{window.location.origin}/api/oauth/calendar/callback</code></p>
        <div className="flex gap-2 mt-4">
          {cfg.google_connected
            ? <button onClick={disconnectGoogle} data-testid="google-disconnect" className="eyw-btn-ghost !py-2 text-sm">Disconnect</button>
            : <button onClick={connectGoogle} disabled={connecting || !cfg.google_client_id || !cfg.google_client_secret_set} data-testid="google-connect" className="eyw-btn !py-2 text-sm disabled:opacity-50">{connecting ? <Loader2 className="animate-spin" size={15} /> : <Link2 size={15} />} Connect Google account</button>}
        </div>
      </div>

      {/* Google Chat */}
      <div className="eyw-card p-6">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-3"><MessageSquare size={18} className="text-[#144F75]" /> Google Chat notifications {cfg.google_chat_webhook_set && <span className="text-xs bg-[#5C8A72]/15 text-[#5C8A72] px-2 py-0.5 rounded-full">configured</span>}</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Get a message in a Google Chat space on new bookings & sales. Paste an <b>Incoming Webhook</b> URL. Optionally route bookings and sales to separate spaces.</p>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Default webhook" type="password" value={form.google_chat_webhook} onChange={(v) => setForm({ ...form, google_chat_webhook: v })} placeholder={cfg.google_chat_webhook_set ? "•••••• (saved)" : "https://chat.googleapis.com/v1/spaces/..."} testid="chat-webhook" />
          <Field label="Bookings channel (optional)" type="password" value={form.google_chat_bookings_webhook} onChange={(v) => setForm({ ...form, google_chat_bookings_webhook: v })} placeholder={cfg.google_chat_bookings_webhook_set ? "•••••• (saved)" : "bookings space webhook"} testid="chat-bookings-webhook" />
          <Field label="Sales channel (optional)" type="password" value={form.google_chat_sales_webhook} onChange={(v) => setForm({ ...form, google_chat_sales_webhook: v })} placeholder={cfg.google_chat_sales_webhook_set ? "•••••• (saved)" : "sales space webhook"} testid="chat-sales-webhook" />
        </div>
        {cfg.google_chat_webhook_set && <button onClick={testChat} data-testid="chat-test" className="eyw-btn-ghost !py-2 text-sm mt-3">Send test message</button>}
      </div>

      {/* Zoom */}
      <div className="eyw-card p-6">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-3"><Video size={18} className="text-[#144F75]" /> Zoom {cfg.zoom_configured
          ? <span className="text-xs bg-[#5C8A72]/15 text-[#5C8A72] px-2 py-0.5 rounded-full" data-testid="zoom-status">configured</span>
          : <span className="text-xs bg-[#D98E73]/15 text-[#B5794E] px-2 py-0.5 rounded-full" data-testid="zoom-status">not set</span>}</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Server-to-Server OAuth. Once verified, online sessions & appointments automatically get a Zoom join link.</p>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Zoom Account ID" value={form.zoom_account_id} onChange={(v) => setForm({ ...form, zoom_account_id: v })} testid="zoom-account-id" />
          <Field label="Zoom Client ID" value={form.zoom_client_id} onChange={(v) => setForm({ ...form, zoom_client_id: v })} testid="zoom-client-id" />
          <Field label="Zoom Client Secret" type="password" value={form.zoom_client_secret} onChange={(v) => setForm({ ...form, zoom_client_secret: v })} placeholder={cfg.zoom_client_secret_set ? "•••••• (saved)" : ""} testid="zoom-client-secret" />
        </div>
        {cfg.zoom_configured && <button onClick={testZoom} data-testid="zoom-test" className="eyw-btn-ghost !py-2 text-sm mt-3">Verify Zoom credentials</button>}
      </div>

      {/* Email (Resend) */}
      <div className="eyw-card p-6">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-3"><Mail size={18} className="text-[#144F75]" /> Email receipts (Resend) {cfg.resend_api_key_set && <span className="text-xs bg-[#5C8A72]/15 text-[#5C8A72] px-2 py-0.5 rounded-full" data-testid="email-status">configured</span>}</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Automatically email a receipt with digital-download links after each order & POS sale. Paste your <b>Resend API key</b> and a verified from-address.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Resend API key" type="password" value={form.resend_api_key} onChange={(v) => setForm({ ...form, resend_api_key: v })} placeholder={cfg.resend_api_key_set ? "•••••• (saved)" : "re_..."} testid="resend-key" />
          <Field label="From email" value={form.resend_from_email} onChange={(v) => setForm({ ...form, resend_from_email: v })} placeholder="receipts@yourdomain.com" testid="resend-from" hint="Use onboarding@resend.dev for testing, or a verified domain sender for production." />
        </div>
        <div className="mt-4">
          <label className="text-xs text-[#4A5D6B] block mb-1">Receipt promo footer (optional)</label>
          <textarea value={form.receipt_promo} onChange={(e) => setForm({ ...form, receipt_promo: e.target.value })} rows={3} data-testid="receipt-promo"
            placeholder="e.g. Members save 10% on every session — join the Community tier at enhancingyourworld.com/memberships" className="eyw-input w-full" />
          <p className="text-[11px] text-[#8FA89B] mt-1">Appended to every order & POS email receipt. Plain text or simple HTML (e.g. &lt;a href&gt; links) supported.</p>
        </div>
        <div className="mt-4">
          <label className="text-xs text-[#4A5D6B] block mb-1">Minimum-exempt promo codes (optional)</label>
          <input value={form.min_exempt_promo_codes} onChange={(e) => setForm({ ...form, min_exempt_promo_codes: e.target.value })} data-testid="min-exempt-promo-codes"
            placeholder="e.g. FOUNDER, VIP2026, STAFFCOMP" className="eyw-input w-full uppercase" />
          <p className="text-[11px] text-[#8FA89B] mt-1">Comma-separated codes that let a guest check out below the $20.00 cart minimum. Admins always bypass the minimum.</p>
        </div>
        <div className="mt-4">
          <label className="text-xs text-[#4A5D6B] block mb-1">Low-stock alert email</label>
          <input value={form.low_stock_email} onChange={(e) => setForm({ ...form, low_stock_email: e.target.value })} data-testid="low-stock-email"
            placeholder="inventory@yourdomain.com" className="eyw-input w-full" />
          <p className="text-[11px] text-[#8FA89B] mt-1">When a tracked product hits its reorder threshold, we email this address (requires Resend key).</p>
        </div>
        {cfg.resend_api_key_set && <button onClick={testEmail} data-testid="email-test" className="eyw-btn-ghost !py-2 text-sm mt-3">Send test email</button>}
        <div className="mt-4 pt-4 border-t border-[#E5E0D8]">
          <label className="text-xs text-[#4A5D6B] block mb-1 font-medium">Weekly community digest</label>
          <p className="text-[11px] text-[#8FA89B] mb-2">Preview the roundup of recent posts &amp; upcoming gatherings before it goes out to opted-in members.</p>
          <label className="flex items-center gap-2 text-sm text-[#1A242B] mb-3">
            <input type="checkbox" checked={!!form.digest_enabled} onChange={(e) => setForm({ ...form, digest_enabled: e.target.checked })} data-testid="digest-enabled" className="accent-[#144F75]" /> Send the weekly digest automatically
          </label>
          {form.digest_enabled && (
            <div className="grid grid-cols-2 gap-3 mb-3 max-w-sm">
              <div>
                <label className="text-[11px] text-[#8FA89B] block mb-1">Day</label>
                <select value={form.digest_day} onChange={(e) => setForm({ ...form, digest_day: Number(e.target.value) })} data-testid="digest-day" className="eyw-input w-full">
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-[#8FA89B] block mb-1">Hour (UTC)</label>
                <select value={form.digest_hour} onChange={(e) => setForm({ ...form, digest_hour: Number(e.target.value) })} data-testid="digest-hour" className="eyw-input w-full">
                  {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                </select>
              </div>
              <p className="col-span-2 text-[11px] text-[#8FA89B]" data-testid="digest-local-hint">
                That's {(() => { const d = new Date(); d.setUTCHours(Number(form.digest_hour) || 0, 0, 0, 0); return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZoneName: "short" }); })()} in your timezone.
              </p>
            </div>
          )}
          <button onClick={previewDigest} data-testid="digest-preview-btn" className="eyw-btn-ghost !py-2 text-sm">Preview weekly digest</button>
        </div>
        <div className="mt-4 pt-4 border-t border-[#E5E0D8]">
          <label className="text-xs text-[#4A5D6B] block mb-1 font-medium">Membership renewal reminders</label>
          <p className="text-[11px] text-[#8FA89B] mb-2">How many days before a member's card is charged should the heads-up email go out?</p>
          <div className="max-w-[200px]">
            <select value={form.renewal_reminder_days} onChange={(e) => setForm({ ...form, renewal_reminder_days: Number(e.target.value) })} data-testid="renewal-reminder-days" className="eyw-input w-full">
              {[1, 2, 3, 4, 5, 7, 10, 14].map((d) => <option key={d} value={d}>{d} day{d === 1 ? "" : "s"} before</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* YouTube Sync */}
      <div className="eyw-card p-6">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-3"><Youtube size={18} className="text-[#FF0000]" /> YouTube Sync {cfg.youtube_api_key_set && <span className="text-xs bg-[#5C8A72]/15 text-[#5C8A72] px-2 py-0.5 rounded-full" data-testid="youtube-status">configured</span>}</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Let enhancers pull their latest YouTube videos into their profile & the media library. Create a <b>YouTube Data API v3</b> key in the Google Cloud Console (APIs &amp; Services → Credentials) after enabling the "YouTube Data API v3".</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="YouTube Data API key" type="password" value={form.youtube_api_key} onChange={(v) => setForm({ ...form, youtube_api_key: v })} placeholder={cfg.youtube_api_key_set ? "•••••• (saved — leave blank to keep)" : "AIza..."} testid="youtube-key" />
        </div>
      </div>


      {/* SMS (Twilio) */}
      <div className="eyw-card p-6">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-3"><MessageSquare size={18} className="text-[#144F75]" /> SMS reminders (Twilio) {cfg.twilio_configured && <span className="text-xs bg-[#5C8A72]/15 text-[#5C8A72] px-2 py-0.5 rounded-full" data-testid="sms-status">configured</span>}</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Send automatic booking reminders by text (24h &amp; 1h before) to clients and enhancers. Add your <b>Twilio</b> credentials — either your Account Auth Token, or a standard API Key + Secret. Until configured, reminders send by email only.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Account SID" value={form.twilio_account_sid} onChange={(v) => setForm({ ...form, twilio_account_sid: v })} placeholder="AC..." testid="twilio-sid" />
          <Field label="Auth Token" type="password" value={form.twilio_auth_token} onChange={(v) => setForm({ ...form, twilio_auth_token: v })} placeholder={cfg.twilio_auth_token_set ? "•••••• (saved — leave blank to keep)" : "your Account auth token"} testid="twilio-token" />
          <Field label="API Key SID (optional)" value={form.twilio_api_key_sid} onChange={(v) => setForm({ ...form, twilio_api_key_sid: v })} placeholder="SK..." testid="twilio-key-sid" hint="Use a standard API Key (not one with Public Key client validation), or leave blank and use the Auth Token." />
          <Field label="API Key Secret (optional)" type="password" value={form.twilio_api_key_secret} onChange={(v) => setForm({ ...form, twilio_api_key_secret: v })} placeholder={cfg.twilio_api_key_secret_set ? "•••••• (saved)" : "API key secret"} testid="twilio-key-secret" />
          <Field label="Sender number" value={form.twilio_from} onChange={(v) => setForm({ ...form, twilio_from: v })} placeholder="+15551234567" testid="twilio-from" hint="Your SMS-capable Twilio number in E.164 format." />
        </div>
        {cfg.twilio_configured && <button onClick={testSms} data-testid="sms-test" className="eyw-btn-ghost !py-2 text-sm mt-3">Send test SMS</button>}
      </div>

      {/* PayPal + Venmo */}
      <div className="eyw-card p-6" data-testid="paypal-section">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-3"><Banknote size={18} className="text-[#003087]" /> PayPal &amp; Venmo {cfg.paypal_configured && <span className="text-xs bg-[#5C8A72]/15 text-[#5C8A72] px-2 py-0.5 rounded-full" data-testid="paypal-status">configured</span>}</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Accept PayPal &amp; Venmo at checkout and send partner payouts to PayPal/Venmo. Create an app at <b>developer.paypal.com</b> (My Apps &amp; Credentials) and paste its <b>Client ID</b> and <b>Secret</b>. Choose <b>Live</b> when ready to take real payments. Venmo appears automatically for eligible US buyers.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Client ID" value={form.paypal_client_id} onChange={(v) => setForm({ ...form, paypal_client_id: v })} placeholder="A21AA...client-id" testid="paypal-client-id" />
          <Field label="Secret" type="password" value={form.paypal_secret} onChange={(v) => setForm({ ...form, paypal_secret: v })} placeholder={cfg.paypal_secret_set ? "•••••• (saved — leave blank to keep)" : "EL...secret"} testid="paypal-secret" />
          <div>
            <label className="block text-sm text-[#4A5D6B] mb-1">Environment</label>
            <select value={form.paypal_mode} onChange={(e) => setForm({ ...form, paypal_mode: e.target.value })} data-testid="paypal-mode" className="eyw-input w-full">
              <option value="sandbox">Sandbox (testing)</option>
              <option value="live">Live (real money)</option>
            </select>
          </div>
        </div>
        {cfg.paypal_configured && <button onClick={testPaypal} data-testid="paypal-test" className="eyw-btn-ghost !py-2 text-sm mt-3">Test PayPal connection</button>}
      </div>

      {/* Booking policy */}
      <div className="eyw-card p-6" data-testid="booking-policy-card">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-3"><CalendarClock size={18} className="text-[#144F75]" /> Booking policy</h3>
        <p className="text-sm text-[#4A5D6B] mb-3">Shop-wide defaults. Every center &amp; Enhancer inherits these unless they set their own policy. Too-soon time slots are hidden at booking, and the cancellation terms below drive the fee members see when they cancel.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-[#4A5D6B] block mb-1">Minimum notice (hours)</label>
            <input type="number" min="0" max="720" value={notice} onChange={(e) => setNotice(e.target.value)} data-testid="min-notice-input" className="eyw-input w-32" />
          </div>
          <div>
            <label className="text-xs text-[#4A5D6B] block mb-1">Free cancellation window (hours)</label>
            <input type="number" min="0" max="8760" value={cancelWindow} onChange={(e) => setCancelWindow(e.target.value)} data-testid="cancel-window-input" className="eyw-input w-40" />
          </div>
          <div>
            <label className="text-xs text-[#4A5D6B] block mb-1">Late cancellation fee ($)</label>
            <input type="number" min="0" step="0.01" value={cancelFee} onChange={(e) => setCancelFee(e.target.value)} data-testid="cancel-fee-input" className="eyw-input w-36" />
          </div>
          <button onClick={saveNotice} disabled={savingNotice} data-testid="save-booking-policy" className="eyw-btn !py-2 text-sm disabled:opacity-60">
            {savingNotice ? <Loader2 className="animate-spin" size={15} /> : null} Save policy
          </button>
        </div>
        <p className="text-[11px] text-[#8FA89B] mt-2">Cancel inside the window and the member forfeits their deposit; a fee above the deposit is added to their balance. Set the window to 0 to allow free cancellation any time.</p>
      </div>

      <PolicyOverridesCard />

      {/* Google Drive backup (signed agreements/waivers) */}
      <div className="eyw-card p-6" data-testid="drive-card">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-3"><HardDrive size={18} className="text-[#144F75]" /> Signed-agreement backup (Google Drive)
          {drive?.connected
            ? <span className="text-xs bg-[#5C8A72]/15 text-[#5C8A72] px-2 py-0.5 rounded-full" data-testid="drive-status">connected</span>
            : <span className="text-xs bg-[#D98E73]/15 text-[#B5794E] px-2 py-0.5 rounded-full" data-testid="drive-status">not connected</span>}
        </h3>
        <p className="text-sm text-[#4A5D6B] mb-3">Every signed agreement/waiver is saved in the app database as a PDF (full text + signer, signature, version, date &amp; IP) and automatically backed up to your Google Drive (into a folder named <b>“EYW Signed Agreements”</b>).</p>
        {drive?.connected ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-[#1A242B]" data-testid="drive-email">Connected as <b>{drive.email}</b></span>
            <button onClick={testDrive} disabled={driveTesting} data-testid="drive-test" className="eyw-btn !py-2 text-sm disabled:opacity-60">
              {driveTesting ? <Loader2 className="animate-spin" size={15} /> : <HardDrive size={15} />} Run backup test
            </button>
            <button onClick={disconnectDrive} data-testid="drive-disconnect" className="eyw-btn-ghost !py-2 text-sm">Disconnect</button>
          </div>
        ) : (
          <div>
            <div className="rounded-xl bg-[#144F75]/[0.04] border border-[#144F75]/10 p-4 text-sm text-[#4A5D6B] space-y-1.5 mb-3" data-testid="drive-setup">
              <p className="font-medium text-[#1A242B]">One-time Google Cloud setup:</p>
              <p>1. Enable the <b>Google Drive API</b> in your project.</p>
              <p>2. Add this authorized redirect URI to your OAuth client: <code className="text-xs">{`${(process.env.REACT_APP_BACKEND_URL || "")}/api/google/drive/callback`}</code></p>
              <p>3. Ensure the <code>…/auth/drive.file</code> scope is allowed on your consent screen (it's non-sensitive — no verification needed).</p>
              <p className="text-xs text-[#8FA89B]">Backups go to your own Drive using your storage. Until connected, signing still works and each PDF is safely stored in the app database.</p>
            </div>
            <button onClick={connectDrive} data-testid="drive-connect" className="eyw-btn !py-2 text-sm"><HardDrive size={15} /> Connect Google Drive</button>
          </div>
        )}
      </div>

      <button onClick={save} disabled={saving} data-testid="integrations-save" className="eyw-btn disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <PlugZap size={18} />} Save integration settings
      </button>
    </div>
  );
}
